(() => {
  if (customElements.get('bf-savings')) return;
  class BFSavings extends window.BFOfferElement {
    connectedCallback() {
      super.connectedCallback();
      this.copyButton = this.querySelector('[data-copy]');
      this.copying = false;
      clearTimeout(this.copyTimer);
      if (this.copyButton) {
        this.copyButton.disabled = !this.code;
        this.copyButton.classList.remove('is-copied');
        this.copyButton.querySelector('[data-label]').textContent = this.message('copyLabel');
      }
      cancelAnimationFrame(this.spreadFrame);
      this.spreadFrame = 0;
      this.spreadAnchor = this.querySelector('[data-spread-anchor]');
      this.spreadPair = this.querySelector('[data-spread]');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      const { signal } = this.lifecycle;
      this.winnerTabs = [...this.querySelectorAll('[data-week-tab]')];
      this.winnerTabs.forEach((tab) => { tab.disabled = false; });
      this.addEventListener('keydown', (event) => {
        const tab = event.target.closest('[data-week-tab]');
        if (!tab || event.altKey || event.ctrlKey || event.metaKey) return;
        const index = this.winnerTabs.indexOf(tab);
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % this.winnerTabs.length;
        else if (event.key === 'ArrowLeft') next = (index - 1 + this.winnerTabs.length) % this.winnerTabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = this.winnerTabs.length - 1;
        else return;
        event.preventDefault();
        this.selectWinnerTab(this.winnerTabs[next], true);
      }, { signal });
      this.addEventListener('shopify:block:select', (event) => {
        const tab = this.winnerTabs.find((item) => item.dataset.weekId === event.detail?.blockId);
        if (tab) this.selectWinnerTab(tab);
      }, { signal });
      const requestSpread = () => {
        if (!this.spreadFrame) this.spreadFrame = requestAnimationFrame(() => this.updateSpread());
      };
      window.addEventListener('scroll', requestSpread, { signal, passive: true });
      window.addEventListener('resize', requestSpread, { signal, passive: true });
      this.reducedMotion.addEventListener('change', requestSpread, { signal });
      this.spreadResizeObserver?.disconnect();
      this.spreadResizeObserver = new ResizeObserver(requestSpread);
      if (this.spreadAnchor) this.spreadResizeObserver.observe(this.spreadAnchor);
      this.updateSpread();
      this.querySelectorAll('[data-card]').forEach((card) => {
        const flipped = card.classList.contains('is-flipped');
        card.querySelectorAll('[data-face]').forEach((face) => {
          const hidden = (face.dataset.face === 'back') !== flipped;
          face.inert = hidden;
          face.setAttribute('aria-hidden', String(hidden));
        });
      });
    }
    updateSpread() {
      this.spreadFrame = 0;
      if (!this.spreadAnchor || !this.spreadPair) return;
      // Measure the untransformed wrapper so rotation cannot feed back into progress.
      const start = window.innerHeight * 1.08;
      const end = window.innerHeight * .60;
      const progress = this.reducedMotion.matches || window.innerWidth <= 700 ? 0 :
        Math.max(0, Math.min(1, (start - this.spreadAnchor.getBoundingClientRect().top) / (start - end)));
      const round = (value) => Math.round(value * 100) / 100;
      this.spreadPair.style.setProperty('--bf-left-x', `${round(-15 * progress)}px`);
      this.spreadPair.style.setProperty('--bf-right-x', `${round(15 * progress)}px`);
      this.spreadPair.style.setProperty('--bf-pair-angle', `${round(-4 * progress)}deg`);
      this.spreadPair.style.setProperty('--bf-right-angle', `${round(9 * progress)}deg`);
    }
    disconnectedCallback() {
      clearTimeout(this.copyTimer);
      cancelAnimationFrame(this.spreadFrame);
      this.spreadFrame = 0;
      this.spreadResizeObserver?.disconnect();
      super.disconnectedCallback();
    }
    onClick(event) {
      super.onClick(event);
      const copy = event.target.closest('[data-copy]');
      if (copy) this.copyCode(copy);
      const weekTab = event.target.closest('[data-week-tab]');
      if (weekTab) this.selectWinnerTab(weekTab);
      const toggle = event.target.closest('[data-flip]');
      const face = event.target.closest('[data-face]');
      const card = (toggle || face)?.closest('[data-card]');
      const surfaceClick = face && (face.dataset.face === 'back') === card?.classList.contains('is-flipped') &&
        !event.target.closest('a, button, input, select, textarea, label, summary, [role="button"], [contenteditable]:not([contenteditable="false"])') &&
        window.getSelection()?.isCollapsed !== false;
      if (card && (toggle || surfaceClick)) {
        const flipped = card.classList.toggle('is-flipped');
        card.querySelectorAll('[data-face]').forEach((face) => {
          const hidden = (face.dataset.face === 'back') !== flipped;
          face.inert = hidden;
          face.setAttribute('aria-hidden', String(hidden));
        });
        card.querySelector(`[data-face="${flipped ? 'back' : 'front'}"] [data-flip]`).focus({ preventScroll: true });
      }
      const dialog = this.querySelector('dialog');
      if (event.target.closest('[data-details]') && dialog) dialog.showModal();
      if (event.target.closest('[data-close-details]') && dialog) dialog.close();
    }
    selectWinnerTab(selected, focus = false) {
      this.winnerTabs.forEach((tab) => {
        const active = tab === selected;
        tab.setAttribute('aria-selected', String(active));
        tab.tabIndex = active ? 0 : -1;
      });
      this.querySelectorAll('[data-week-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.weekId !== selected.dataset.weekId;
      });
      if (focus) {
        selected.focus({ preventScroll: true });
        selected.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
    async copyCode(button) {
      if (this.copying || !this.code) return;
      this.copying = true;
      this.lastTrigger = button;
      const lifecycle = this.lifecycle;
      try {
        await navigator.clipboard.writeText(this.code);
        if (!this.isConnected || this.lifecycle !== lifecycle) return;
        button.classList.add('is-copied');
        button.querySelector('[data-label]').textContent = this.message('copiedLabel');
        this.notify(this.message('copySuccessMessage'), 'success');
        clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          button.classList.remove('is-copied');
          button.querySelector('[data-label]').textContent = this.message('copyLabel');
        }, 6000);
      } catch {
        if (this.isConnected && this.lifecycle === lifecycle) {
          this.notify(this.message('copyErrorMessage').replaceAll('[code]', this.code), 'error');
        }
      } finally {
        if (this.lifecycle === lifecycle) this.copying = false;
      }
    }
  }
  customElements.define('bf-savings', BFSavings);
})();
